import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { api } from '@/services/api';
import type { ReferenceDataItem } from '@/services/api';
import {
  OFFICE_LABELS as FALLBACK_OFFICE_LABELS,
  MEMBER_STATUS_LABELS as FALLBACK_STATUS_LABELS,
  CLUB_1855_LABELS as FALLBACK_1855_LABELS,
  UNDERGRADUATE_OFFICES as FALLBACK_CHAPTER_OFFICES,
  HOUSE_CORP_OFFICES as FALLBACK_HOUSE_CORP_OFFICES,
  NATIONAL_OFFICES as FALLBACK_NATIONAL_OFFICES,
  ACTIVE_STATUSES as FALLBACK_ACTIVE_STATUSES,
} from '@/types';

export const useReferenceDataStore = defineStore('referenceData', () => {
  const offices = ref<ReferenceDataItem[]>([]);
  const statuses = ref<ReferenceDataItem[]>([]);
  const club1855 = ref<ReferenceDataItem[]>([]);
  const employers = ref<ReferenceDataItem[]>([]);
  const siteConfig = ref<Record<string, boolean>>({});
  const isLoaded = ref(false);
  const isLoading = ref(false);

  // --- Getters ---

  const officeLabels = computed<Record<string, string>>(() => {
    if (offices.value.length === 0) return FALLBACK_OFFICE_LABELS;
    const map: Record<string, string> = {};
    for (const item of offices.value) {
      map[item.code] = item.label;
    }
    return map;
  });

  const statusLabels = computed<Record<string, string>>(() => {
    if (statuses.value.length === 0) return FALLBACK_STATUS_LABELS;
    const map: Record<string, string> = {};
    for (const item of statuses.value) {
      map[item.code] = item.label;
    }
    return map;
  });

  const club1855Labels = computed<Record<string, string>>(() => {
    if (club1855.value.length === 0) return FALLBACK_1855_LABELS;
    const map: Record<string, string> = {};
    for (const item of club1855.value) {
      map[item.code] = item.label;
    }
    return map;
  });

  const chapterOffices = computed<string[]>(() => {
    if (offices.value.length === 0) return FALLBACK_CHAPTER_OFFICES;
    return offices.value
      .filter(o => o.category === 'chapter' && o.active !== false)
      .sort((a, b) => a.label.localeCompare(b.label))
      .map(o => o.code);
  });

  const houseCorpOffices = computed<string[]>(() => {
    if (offices.value.length === 0) return FALLBACK_HOUSE_CORP_OFFICES;
    return offices.value
      .filter(o => o.category === 'housecorp' && o.active !== false)
      .sort((a, b) => a.label.localeCompare(b.label))
      .map(o => o.code);
  });

  const nationalOffices = computed<string[]>(() => {
    if (offices.value.length === 0) return FALLBACK_NATIONAL_OFFICES;
    return offices.value
      .filter(o => o.category === 'national' && o.active !== false)
      .sort((a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999) || a.label.localeCompare(b.label))
      .map(o => o.code);
  });

  const allNationalOffices = computed<string[]>(() => {
    if (offices.value.length === 0) return FALLBACK_NATIONAL_OFFICES;
    return offices.value
      .filter(o => o.category === 'national')
      .sort((a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999) || a.label.localeCompare(b.label))
      .map(o => o.code);
  });

  const statusOptions = computed(() => {
    if (statuses.value.length === 0) {
      return Object.entries(FALLBACK_STATUS_LABELS).map(([value, label]) => ({ value, label }));
    }
    return statuses.value
      .filter(s => s.active !== false)
      .sort((a, b) => a.label.localeCompare(b.label))
      .map(s => ({ value: s.code, label: s.label }));
  });

  const club1855Options = computed(() => {
    if (club1855.value.length === 0) {
      return Object.entries(FALLBACK_1855_LABELS).map(([value, label]) => ({ value, label }));
    }
    return club1855.value
      .filter(s => s.active !== false)
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .map(s => ({ value: s.code, label: s.label }));
  });

  const officeSortOrder = computed<Record<string, number>>(() => {
    const map: Record<string, number> = {};
    for (const item of offices.value) {
      map[item.code] = item.sortOrder ?? 999;
    }
    return map;
  });

  const activeStatuses = computed<string[]>(() => {
    if (statuses.value.length === 0) return FALLBACK_ACTIVE_STATUSES;
    return statuses.value
      .filter(s => s.active !== false)
      .map(s => s.code);
  });

  const employerNames = computed<string[]>(() =>
    employers.value
      .filter(e => e.active !== false)
      .sort((a, b) => a.label.localeCompare(b.label))
      .map(e => e.label)
  );

  // All offices (including inactive) for display purposes
  const allChapterOffices = computed<string[]>(() => {
    if (offices.value.length === 0) return FALLBACK_CHAPTER_OFFICES;
    return offices.value
      .filter(o => o.category === 'chapter')
      .sort((a, b) => a.label.localeCompare(b.label))
      .map(o => o.code);
  });

  const allHouseCorpOffices = computed<string[]>(() => {
    if (offices.value.length === 0) return FALLBACK_HOUSE_CORP_OFFICES;
    return offices.value
      .filter(o => o.category === 'housecorp')
      .sort((a, b) => a.label.localeCompare(b.label))
      .map(o => o.code);
  });

  // --- Actions ---

  async function load() {
    if (isLoaded.value || isLoading.value) return;
    isLoading.value = true;
    try {
      const data = await api.getReferenceData();
      offices.value = data.offices || [];
      statuses.value = data.statuses || [];
      club1855.value = data.levels || [];
      employers.value = data.employers || [];
      isLoaded.value = true;
    } catch (err) {
      console.warn('Failed to load reference data, using fallback constants:', err);
    } finally {
      isLoading.value = false;
    }
  }

  async function saveItem(data: {
    type: string;
    code: string;
    label: string;
    sortOrder?: number;
    active?: boolean;
    category?: string;
    monthlyAmount?: number;
    annualAmount?: number;
  }) {
    const result = await api.putReferenceData(data);

    // Update local state
    const item = result.item;
    const list = getListForType(data.type);
    const idx = list.value.findIndex(i => i.code === data.code);
    if (idx >= 0) {
      list.value[idx] = item;
    } else {
      list.value.push(item);
    }

    return item;
  }

  async function deleteItem(type: string, code: string) {
    await api.deleteReferenceData(type, code);

    // Update local state
    const list = getListForType(type);
    list.value = list.value.filter(i => i.code !== code);
  }

  function getListForType(type: string) {
    switch (type) {
      case 'office': return offices;
      case 'status': return statuses;
      case '1855': return club1855;
      case 'employer': return employers;
      default: return offices;
    }
  }

  async function loadConfig(key: string): Promise<boolean> {
    try {
      const result = await api.getConfig(key);
      console.log(`[loadConfig] key=${key}, result.value=${result.value}, type=${typeof result.value}`);
      siteConfig.value[key] = result.value;
      console.log(`[loadConfig] After setting, siteConfig[${key}]=${siteConfig.value[key]}`);
      return result.value;
    } catch (err) {
      console.warn(`Failed to load config ${key}, using default:`, err);
      return key === 'require-approval' ? true : false;
    }
  }

  async function updateConfig(key: string, value: boolean): Promise<void> {
    await api.putConfig(key, value);
    siteConfig.value[key] = value;
  }

  return {
    offices,
    statuses,
    club1855,
    employers,
    siteConfig,
    isLoaded,
    isLoading,
    officeLabels,
    statusLabels,
    club1855Labels,
    chapterOffices,
    houseCorpOffices,
    nationalOffices,
    allChapterOffices,
    allHouseCorpOffices,
    allNationalOffices,
    statusOptions,
    club1855Options,
    officeSortOrder,
    activeStatuses,
    employerNames,
    load,
    saveItem,
    deleteItem,
    loadConfig,
    updateConfig,
  };
});
